import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, MapPin, Briefcase, FileText, Search, ChevronDown, Loader2, ArrowLeft } from 'lucide-react';
import { createSubJob, getJobs } from '../../services/jobsapi';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Job {
  id: string;
  title: string;
  location: string;
  companyName: string;
  workType: string;
  status: string;
  employmentType: string;
  createdAt: string;
  noOfOpenings: number;
}

interface SubjobFormData {
  parentJobId: string;
  title: string;
  description: string;
  keywordsForSeo: string[];
  location: string;
}

const CreateSubjobPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SubjobFormData>({
    parentJobId: '',
    title: '',
    description: '',
    keywordsForSeo: [],
    location: '',
  });
  const [keywordInput, setKeywordInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isJobsLoading, setIsJobsLoading] = useState<boolean>(true);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) || job.location.toLowerCase().includes(searchTerm.toLowerCase()) || job.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedJob = jobs.find(job => job.id === formData.parentJobId);
  // Predefined skills array
  const fetchParentJobs = async (params : any) => {
    try {
      setIsJobsLoading(true);
      const response = await getJobs(params);
      const newJobs = response.data.jobs || [];
      
      // If we have a selected job and it's not in the new results, add it
      if (formData.parentJobId && !newJobs.find(job => job.id === formData.parentJobId)) {
        // Find the selected job from the previous jobs array
        const selectedJobFromPrevious = jobs.find(job => job.id === formData.parentJobId);
        if (selectedJobFromPrevious) {
          // Create a complete job object with all required properties
          const completeJob = {
            ...selectedJobFromPrevious,
            workType: selectedJobFromPrevious.workType || 'Full-time',
            status: selectedJobFromPrevious.status || 'published',
            employmentType: selectedJobFromPrevious.employmentType || 'permanent',
            createdAt: selectedJobFromPrevious.createdAt || new Date().toISOString(),
            noOfOpenings: selectedJobFromPrevious.noOfOpenings || 1
          };
          // Add the selected job to the beginning of the new results
          newJobs.unshift(completeJob);
        }
      }
      
      setJobs(newJobs);
      setIsJobsLoading(false);
    } catch (err) {
      setError('Failed to load parent jobs');
      setIsJobsLoading(false);
    }
  };
  useEffect(() => {
    
    fetchParentJobs({ status: 'published' });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

   useEffect(() => {
    const handler = setTimeout(() => {
      // Only fetch if the search term is not empty (optional)
      fetchParentJobs({ status: "published", search: searchTerm });
    }, 500); // 500ms delay

    // Cleanup previous timeout if user keeps typing
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSelect = (jobId: string) => {
    setFormData({ ...formData, parentJobId: jobId });
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredJobs.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredJobs.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredJobs[focusedIndex]) {
          handleSelect(filteredJobs[focusedIndex].id);
        }
        break;
    }
  };

  const TITLE_DISALLOWED_CHARS = /[%&#/]/g;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'title' ? value.replace(TITLE_DISALLOWED_CHARS, '') : value;
    setFormData((prev) =>
      {
        console.log('Prev:', prev);
        return { ...prev, [name]: sanitizedValue }
      });
    if (error) setError(null);
  };

 const handleAddKeyword = () => {
  const keyword = keywordInput.trim();

  if (
    keyword &&
    !formData.keywordsForSeo.some(
      (k) => k.toLowerCase() === keyword.toLowerCase()
    )
  ) {
    setFormData((prev) => ({
      ...prev,
      keywordsForSeo: [...prev.keywordsForSeo, keyword],
    }));
    setKeywordInput("");
    setError(null);
  } else if (
    formData.keywordsForSeo.some(
      (k) => k.toLowerCase() === keyword.toLowerCase()
    )
  ) {
    setError("Keyword already added");
    setTimeout(() => setError(null), 3000);
  }
};

  const handleRemoveKeyword = (keyword: string) => {
    setFormData((prev) => ({
      ...prev,
      keywordsForSeo: prev.keywordsForSeo.filter((k) => k !== keyword),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.parentJobId) {
      setError('Parent Job ID is required');
      return;
    }
    if (!formData.title) {
      setError('Job Title is required');
      return;
    }
    if (!hasValidContent(formData.description)) {
      setError('Job Description is required');
      return;
    }
    if (formData.keywordsForSeo.length === 0) {
      setError('At least one SEO keyword is required');
      return;
    }
    if (!formData.location) {
      setError('Location is required');
      return;
    }

    setIsLoading(true);
    try {
      await createSubJob(formData);
      toast.success('Subjob created successfully!', {
        position: 'top-right',
        duration: 3000,
      });
      setIsLoading(false);
      navigate('/viewSubJobs');
    } catch (error) {
      console.error('Error creating subjob:', error);
      setError('Failed to create subjob');
      setIsLoading(false);
    }
  };

  // Helper function to check if HTML content has actual text (not just tags)
  const hasValidContent = (htmlContent: string) => {
    if (!htmlContent) return false;
    // Remove HTML tags and check if there's actual text content
    const textContent = htmlContent.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  };

  const isFormValid =
    formData.parentJobId.trim() !== '' &&
    formData.title.trim() !== '' &&
    hasValidContent(formData.description) &&
    formData.keywordsForSeo.length > 0 &&
    formData.location.trim() !== '';

  return (
    <>
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
        <div className="mb-8 pb-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full text-orange-600 hover:bg-orange-50 transition-all duration-200 mr-2"
                aria-label="Go back"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="w-12 h-12 bg-[#F9781C] rounded-2xl flex items-center justify-center mr-4">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Create New Subjob
                </h1>
                <p className="text-gray-600 mt-1">Add a new subjob to expand your team's capabilities</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-2">
            <label className="flex items-center text-sm font-semibold text-gray-700">
              <FileText className="w-4 h-4 mr-2 text-gray-500" />
              Parent Job <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <div
                className={`relative w-full px-4 py-4 bg-white border-2 rounded-2xl cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl ${
                  isOpen 
                    ? 'border-blue-500 ring-4 ring-blue-200/50' 
                    : 'border-gray-200 hover:border-blue-300'
                } ${isJobsLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                onClick={() => !isJobsLoading && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Briefcase className="w-4 h-4 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {isJobsLoading ? (
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          <span className="text-gray-500 font-medium">Loading jobs...</span>
                        </div>
                      ) : selectedJob ? (
                        <div>
                          <span className="text-gray-900 font-medium">{selectedJob.title}</span>
                          <div className="text-xs text-gray-500 mt-0.5">Selected Job</div>
                        </div>
                      ) : (
                        <span className="text-gray-500 font-medium">Select a parent job</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`} 
                    />
                  </div>
                </div>
              </div>

              {isOpen && !isJobsLoading && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        ref={searchRef}
                        type="text"
                        placeholder="Search jobs..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setFocusedIndex(0);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
                      />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {jobs.length > 0 ? (
                      jobs.map((job, index) => (
                        <div
                          key={job.id}
                          className={`px-4 py-3 cursor-pointer transition-all duration-150 ${
                            index === focusedIndex 
                              ? 'bg-blue-50 border-r-4 border-blue-500' 
                              : 'hover:bg-gray-50'
                          } ${formData.parentJobId === job.id ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
                          onClick={() => handleSelect(job.id)}
                          role="option"
                          aria-selected={formData.parentJobId === job.id}
                        >
                          <div className="flex items-center space-x-3 gap-4">
                            <div className={`w-2 h-2 rounded-full ${
                              formData.parentJobId === job.id ? 'bg-blue-500' : 'bg-gray-300'
                            }`} />
                            <section className='flex flex-col'>
                              <span className="font-medium">{job.title}</span>
                              
                              <span className="font-medium text-gray-400">{job.companyName} - {job?.location}</span>
                            </section>
                            
                            {/* <span>{JSON.stringify()}</span> */}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="font-medium">No jobs found</p>
                        <p className="text-sm">Try adjusting your search terms</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-semibold text-gray-700">
              <Briefcase className="w-4 h-4 mr-2 text-gray-500" />
              Job Title <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-4 bg-gray-50/50 border-2 border-gray-200 rounded-2xl  focus:ring-4 focus:ring-orange-200/50 transition-all duration-300"
              placeholder="Enter job title (e.g., Senior React Developer)"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-semibold text-gray-700">
              <FileText className="w-4 h-4 mr-2 text-gray-500" />
              Job Description <span className="text-red-500 ml-1">*</span>
            </label>
            <ReactQuill
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Describe the job responsibilities, requirements, and expectations..."
              className="w-full rounded-2xl h-80 mb-6"
              style={{ fontSize: '16px', lineHeight: '1.6' }}
              theme="snow"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['link'],
                  ['clean']
                ],
                clipboard: {
                  matchVisual: false,
                }
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline',
                'list', 'bullet',
                'link'
              ]}
            />
          </div>

          <div className="md:flex md:flex-row flex-col items-start gap-4 pt-8">
            <div className="w-full space-y-4">
              <label className="flex items-center text-sm font-semibold text-gray-700 mt-5">
                <Search className="w-4 h-4 mr-2 text-gray-500" />
                SEO Keywords <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handleAddKeyword)}
                    className="w-full px-4 py-3 bg-gray-50/50 border-2 border-gray-200 rounded-xl focus:border-black focus:ring-4 focus:ring-orange-200/50 transition-all duration-300"
                    placeholder="Add SEO keyword (e.g., remote, frontend)"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className="px-4 py-3 bg-[#F9781C] text-white font-medium rounded-xl hover:bg-[#E8670A] transition-all duration-200 shadow-lg hover:shadow-xl flex items-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {formData.keywordsForSeo.length > 0 && (
                <div className="flex flex-wrap gap-[0.4rem]" style={{marginBottom: '8px', marginTop: '8px'}}>
                  {formData.keywordsForSeo.map((keyword) => (
                    <div
                      key={keyword}
                      className="inline-flex items-center px-3 py-2 bg-orange-50 text-[#F9781C] rounded-full border border-orange-200 hover:shadow-md transition-all duration-200 text-[12px] px-[8px] py-[4px]"
                    >
                      {keyword}
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(keyword)}
                        style={{ minHeight: '20px', minWidth: '20px' }}
                        className="ml-[4px] hover:bg-orange-200 rounded-full transition-colors duration-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 w-full mt-8">
              <label className="flex items-center text-sm font-semibold text-gray-700">
                <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                Location <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-4 bg-gray-50/50 border-2 border-gray-200 rounded-2xl  focus:ring-4 focus:ring-orange-200/50 transition-all duration-300"
                placeholder="Enter location (e.g., New York, NY or Remote)"
                required
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 py-4 border-t border-gray-100 bg-white sticky bottom-0 left-0 w-full flex justify-end">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading || isJobsLoading || !isFormValid}
            className="px-6 py-4 bg-[#F9781C] text-white font-semibold rounded-2xl hover:bg-[#E8670A] hover:shadow-2xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-200/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h-8z"></path>
                </svg>
                Creating Subjob...
              </div>
            ) : (
              'Create Subjob'
            )}
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
            <X className="w-4 h-4 text-red-600" />
          </div>
          <span className="text-red-700 font-medium">{error}</span>
        </div>
      )}
    </>
  );
};

export default CreateSubjobPage;